import { forwardRef, type ReactElement, type ReactNode, type Ref } from 'react';

import { useBehavioralTrackingProps } from '../../../../core/hooks/useBehavioralTrackingProps.js';
import type { BehaviorTrackingProps } from '../../../../core/types/behavior-tracking-props.js';
import type { DataTestId } from '../../../../core/types/data-props.js';
import type { StylingProps } from '../../../../core/types/styling-props.js';
import { devLog } from '../../../../core/utils/logging.js';
import { Flex } from '../../../../layouts/flex/Flex.js';
import { useDataTable } from '../../contexts/DataTableContext.js';
import type { DataTableRowData } from '../../public.api.js';

/**
 * @public
 */
export interface DataTableSelectedRowsActionsProps<
  TData extends DataTableRowData,
>
  extends StylingProps, DataTestId, BehaviorTrackingProps {
  children: (
    rows: {
      rowId: string;
      originalRow: TData;
    }[],
  ) => ReactNode;
}

// A helper function to check whether the child component is the SelectedRowsActions component
/**
 * @internal
 */
export function _isDataTableSelectedRowsActions<TData extends DataTableRowData>(
  child: ReactElement | null,
): child is ReactElement<DataTableSelectedRowsActionsProps<TData>> {
  return child?.type === DataTableSelectedRowsActions;
}

/**
 * Selected rows actions component used for configuring actions
 * that can be applied on one or multiple rows in the DataTable simultaneously.
 * Also, it gets called by DataTable as rows get selected/unselected.
 *
 * @param props - contains children or self
 *
 * @returns DataTableSelectedRowsActions containing children.
 * @public
 */
export const DataTableSelectedRowsActions = /* @__PURE__ */ forwardRef(
  <TData extends DataTableRowData>(
    props: DataTableSelectedRowsActionsProps<TData>,
    forwardedRef: Ref<HTMLDivElement>,
  ): ReactElement | null => {
    const table = useDataTable();

    const {
      children,
      'data-testid': dataTestId,
      className: consumerClassName,
      style: consumerStyle,
      ...remainingProps
    } = props;

    const [behavioralTrackingProps, restProps] =
      useBehavioralTrackingProps(remainingProps);

    if (typeof children !== 'function') {
      devLog('The SelectedRowsActions sub-component child must be a function!');
      return null;
    }

    const currentSelectableRows = table
      .getSelectedRowModel()
      .flatRows.map((row) => ({
        rowId: row.id,
        originalRow: row.original as TData,
      }));

    return (
      <Flex
        ref={forwardedRef}
        justifyContent="flex-start"
        alignItems="flex-end"
        flexWrap="wrap"
        data-testid={dataTestId}
        className={consumerClassName}
        style={consumerStyle}
        {...behavioralTrackingProps}
        {...restProps}
      >
        <Flex alignItems="center" flexWrap="wrap" gap={4}>
          {children(currentSelectableRows)}
        </Flex>
      </Flex>
    );
  },
);

(
  DataTableSelectedRowsActions as typeof DataTableSelectedRowsActions & {
    displayName: string;
  }
).displayName = 'DataTable.SelectedRowsActions';
